import { useEffect } from 'react';
import { message } from 'antd';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND
} from 'lexical';
import { useEditor } from '../editor';

interface LineLimitPluginProps {
  maxLines?: number;
  maxCharsPerLine?: number;
}

export const LineLimitPlugin: React.FC<LineLimitPluginProps> = ({ maxLines = 3, maxCharsPerLine = 18 }) => {
  const { editor } = useEditor();

  useEffect(() => {
    if (!editor) return;

    // 1. Prevenir Enter si ya tiene maxLines
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      event => {
        const lineCount = editor.getEditorState().read(() => {
          const root = $getRoot();
          return root.getChildren().length;
        });

        if (lineCount >= maxLines) {
          event?.preventDefault();
          message.warning(`Máximo ${maxLines} líneas permitidas`);
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );

    // 2. Prevenir escribir si la línea actual superó maxCharsPerLine
    const unregisterUpdate = editor.registerUpdateListener(() => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const anchorNode = selection.anchor.getNode();
        const paragraph = anchorNode.getTopLevelElement();
        if (!paragraph) return;

        const textContent = paragraph.getTextContent();

        // Si supera el límite, truncar
        if (textContent.length > maxCharsPerLine) {
          const truncated = textContent.slice(0, maxCharsPerLine);

          // Reemplazar contenido
          paragraph.clear();
          const newTextNode = $createTextNode(truncated);
          paragraph.append(newTextNode);

          // Mover cursor al FINAL del texto truncado
          selection.anchor.set(newTextNode.getKey(), truncated.length, 'text');
          selection.focus.set(newTextNode.getKey(), truncated.length, 'text');

          message.warning(`Máximo ${maxCharsPerLine} caracteres por línea`);
        }
      });
    });

    return () => {
      unregisterEnter();
      unregisterUpdate();
    };
  }, [editor, maxLines, maxCharsPerLine]);

  return null;
};
